import { useId, useState } from 'react'
import { cn } from '../../lib/cn.js'
import { fileUrl } from '../../lib/files.js'
import { formatDate } from '../../lib/format.js'
import { Icon } from '../Icon.js'
import { Input, Select, Textarea } from '../ui.js'
import { asText, isMulti, optionsOf, shapeOf, toggleChoice } from './model.js'
import type {
  ReportAnswer,
  ReportField,
  ReportHistoryEntry,
  ReportPhoto,
} from './types.js'

/**
 * One question, as a card.
 *
 * There is no termite-specific component in here and there is not meant to be.
 * What makes the seeded termite report read as a purpose-built screen is that
 * six of its answers are a tap, the section headings are the words the office
 * uses, and the long answers arrive half-written — all of which is
 * configuration on `FormField`. A new service type gets the same treatment
 * without a deployment. Decision 54.
 */

export type FieldCardProps = {
  field: ReportField
  answer: ReportAnswer
  photos: ReportPhoto[]
  onChange: (value: ReportAnswer) => void
  /** A contradiction names this field, so the card is outlined amber. */
  flagged?: boolean
  /** Answered, but too short to stand on its own in the report. */
  vague?: boolean
  /** A sentence composed from answers already given, offered and never written. */
  draft?: string | null
  /** What was answered here on earlier visits, newest first. */
  history?: ReportHistoryEntry[]
  onAddPhoto?: () => void
  onRemovePhoto?: (photoId: string) => void
  onCaptionPhoto?: (photoId: string, caption: string) => void
  onSign?: () => void
  /** A completed report reads back but does not take answers. */
  readOnly?: boolean
}

/**
 * The chip a choice is made with.
 *
 * Deliberately not `FilterChip`: that is an 11px pill for a dense list toolbar,
 * and this is the primary control on a screen used one-handed in a roof void.
 * Selected is ink-900, the same rule as everywhere else — green would read as
 * "do this" rather than "this is the answer".
 */
export function Choice({
  selected,
  disabled,
  onClick,
  children,
}: {
  selected: boolean
  disabled?: boolean
  onClick: () => void
  children: string
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'inline-flex min-h-[var(--control-md)] items-center rounded-full px-4 text-sm font-medium',
        'transition-colors disabled:cursor-not-allowed disabled:opacity-60',
        selected
          ? 'bg-brand-800 text-white ring-1 ring-brand-800 ring-inset'
          : 'bg-white text-ink-700 ring-1 ring-ink-200 ring-inset hover:bg-ink-50',
      )}
    >
      {children}
    </button>
  )
}

export function FieldCard({
  field,
  answer,
  photos,
  onChange,
  flagged,
  vague,
  draft,
  history,
  onAddPhoto,
  onRemovePhoto,
  onCaptionPhoto,
  onSign,
  readOnly,
}: FieldCardProps) {
  const controlId = useId()
  const shape = shapeOf(field)
  const options = optionsOf(field)
  const canAddPhoto = Boolean(onAddPhoto) && !readOnly
  // Photos answer a PHOTO field. Anywhere else they are evidence beside the
  // answer, and a photographed question with nothing typed is still unanswered.
  const blank = isBlank(answer, shape === 'photo' ? photos : [])

  if (shape === 'heading') {
    return (
      <h3 className="px-0.5 pt-2 text-[13px] font-semibold text-ink-900">{field.label}</h3>
    )
  }

  return (
    <div
      className={cn(
        'flex flex-col gap-2.5 rounded-[14px] bg-white p-[var(--card-pad)] ring-1 ring-inset',
        flagged ? 'ring-amber-300' : blank ? 'ring-ink-300' : 'ring-ink-200',
      )}
      data-field={field.key}
    >
      <div className="flex items-center gap-2">
        <label
          htmlFor={controlId}
          className="min-w-0 flex-1 text-[12px] font-semibold tracking-[0.025em] text-ink-500 uppercase"
        >
          {field.label}
          {field.required ? <span className="ml-0.5 text-red-600">*</span> : null}
        </label>
        {blank ? (
          <span className="shrink-0 text-[10.5px] font-semibold tracking-[0.04em] text-ink-400 uppercase">
            still blank
          </span>
        ) : null}
      </div>

      {field.helpText ? <p className="text-xs text-ink-500">{field.helpText}</p> : null}

      {/* A checklist of things done — the site safety checks — is the same
          ticks nearly every time, so one control ticks them all. The ticks are
          still the technician's, and each can be taken off. ADR 029. */}
      {shape === 'chips' && field.presentation === 'CHECKLIST' && isMulti(field) && !readOnly ? (
        <button
          type="button"
          aria-pressed={Array.isArray(answer) && answer.length === options.length}
          onClick={() =>
            onChange(Array.isArray(answer) && answer.length === options.length ? [] : [...options])
          }
          className={cn(
            'inline-flex min-h-[var(--control-md)] items-center justify-center gap-2 rounded-[12px] px-4 text-sm font-semibold ring-1 ring-inset transition-colors',
            Array.isArray(answer) && answer.length === options.length
              ? 'bg-brand-800 text-white ring-brand-800'
              : 'bg-white text-brand-800 ring-brand-800 hover:bg-brand-50',
          )}
        >
          <Icon name="check" className="size-4" />
          {Array.isArray(answer) && answer.length === options.length
            ? `All ${options.length} done`
            : `All ${options.length} checks done`}
        </button>
      ) : null}

      {shape === 'chips' ? (
        <div role="group" aria-label={field.label} className="flex flex-wrap gap-2">
          {options.map((option) => (
            <Choice
              key={option}
              selected={isChosen(answer, option)}
              disabled={readOnly}
              onClick={() => onChange(toggleChoice(field, answer, option))}
            >
              {option}
            </Choice>
          ))}
        </div>
      ) : null}

      {shape === 'boolean' ? (
        <div role="group" aria-label={field.label} className="flex flex-wrap gap-2">
          {[
            { label: field.type === 'PASS_FAIL' ? 'Pass' : 'Yes', value: true },
            { label: field.type === 'PASS_FAIL' ? 'Fail' : 'No', value: false },
          ].map((choice) => (
            <Choice
              key={choice.label}
              selected={answer === choice.value}
              disabled={readOnly}
              onClick={() => onChange(answer === choice.value ? null : choice.value)}
            >
              {choice.label}
            </Choice>
          ))}
        </div>
      ) : null}

      {shape === 'dropdown' ? (
        <Select
          id={controlId}
          disabled={readOnly}
          // A multi-select drawn as a dropdown is a native multiple, which is
          // rare enough that only a very long option list reaches it.
          multiple={isMulti(field)}
          value={dropdownValue(field, answer)}
          onChange={(event) =>
            onChange(
              isMulti(field)
                ? [...event.target.selectedOptions].map((option) => option.value)
                : event.target.value === ''
                  ? null
                  : event.target.value,
            )
          }
        >
          {isMulti(field) ? null : <option value="">Not answered</option>}
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Select>
      ) : null}

      {shape === 'text' ? (
        <Input
          id={controlId}
          disabled={readOnly}
          value={typeof answer === 'string' ? answer : ''}
          placeholder={field.helpText ?? ''}
          onChange={(event) => onChange(event.target.value === '' ? null : event.target.value)}
        />
      ) : null}

      {shape === 'textarea' ? (
        <Textarea
          id={controlId}
          rows={3}
          disabled={readOnly}
          value={typeof answer === 'string' ? answer : ''}
          onChange={(event) => onChange(event.target.value === '' ? null : event.target.value)}
        />
      ) : null}

      {shape === 'number' ? (
        <Input
          id={controlId}
          type="number"
          inputMode="decimal"
          disabled={readOnly}
          value={typeof answer === 'number' ? String(answer) : ''}
          onChange={(event) =>
            onChange(event.target.value === '' ? null : Number(event.target.value))
          }
        />
      ) : null}

      {shape === 'date' ? (
        <Input
          id={controlId}
          type="date"
          disabled={readOnly}
          value={dateValue(answer)}
          onChange={(event) =>
            onChange(event.target.value === '' ? null : new Date(event.target.value).toISOString())
          }
        />
      ) : null}

      {shape === 'photo' ? (
        <PhotoGrid
          label={field.label}
          photos={photos}
          readOnly={readOnly}
          {...(onAddPhoto ? { onAdd: onAddPhoto } : {})}
          {...(onRemovePhoto ? { onRemove: onRemovePhoto } : {})}
          {...(onCaptionPhoto ? { onCaption: onCaptionPhoto } : {})}
        />
      ) : null}

      {/* Evidence against the question being answered, on every card.
          A technician photographs the damaged skirting board while looking at
          it, not against a photo question three sections later. The tile-sized
          Add is only on a PHOTO field, where photos are the answer; here it is
          one line so a 28-field report does not open as 28 empty tiles.
          Decision 73. */}
      {shape !== 'photo' && shape !== 'signature' && (photos.length > 0 || canAddPhoto) ? (
        <PhotoGrid
          label={`Photos for ${field.label}`}
          photos={photos}
          readOnly={readOnly}
          compact
          {...(onAddPhoto ? { onAdd: onAddPhoto } : {})}
          {...(onRemovePhoto ? { onRemove: onRemovePhoto } : {})}
          {...(onCaptionPhoto ? { onCaption: onCaptionPhoto } : {})}
        />
      ) : null}

      {shape === 'signature' ? (
        <SignatureBlock
          label={field.label}
          signed={typeof answer === 'string' && answer.length > 0}
          readOnly={readOnly}
          {...(onSign ? { onSign } : {})}
          onClear={() => onChange(null)}
        />
      ) : null}

      {/* A draft is offered, never applied. One tap accepts it and it then reads
          as any other typed answer, which is the point: the technician owns the
          sentence that goes to the client. Decision 55. */}
      {draft && blank && !readOnly ? (
        <button
          type="button"
          onClick={() => onChange(draft)}
          className="min-h-[var(--control-sm)] self-start rounded-full bg-white px-3.5 py-1.5 text-left text-[12.5px] font-medium text-ink-700 ring-1 ring-ink-200 ring-inset transition-colors hover:bg-ink-50"
        >
          Draft this from the answers you already gave
        </button>
      ) : null}

      {vague ? (
        <p className="rounded-[10px] bg-amber-50 px-3 py-2 text-[12.5px] leading-relaxed text-amber-800 ring-1 ring-amber-200 ring-inset">
          Too short to stand on its own in the report. Name where, and what you actually saw.
        </p>
      ) : null}

      {history && history.length > 0 ? (
        <History
          label={field.label}
          entries={history}
          answer={answer}
          readOnly={readOnly ?? false}
          appendable={shape === 'text' || shape === 'textarea'}
          onUse={onChange}
        />
      ) : null}
    </div>
  )
}

/**
 * What was answered here before.
 *
 * The one assist that suggests nothing: it shows the record and leaves the
 * judgement where it belongs. Seeing that the undetected risk at this address was
 * Moderate twice and Low once is exactly the context a technician needs, and it
 * is the opposite of being told what to write.
 *
 * Behind a tap because most of the time it is not the question. A 28-field report
 * with three prior visits would otherwise open as 84 lines of last year.
 */
function History({
  label,
  entries,
  answer,
  readOnly,
  appendable,
  onUse,
}: {
  label: string
  entries: ReportHistoryEntry[]
  answer: ReportAnswer
  readOnly: boolean
  /** Free text can have an earlier answer added to it; a chip can only replace. */
  appendable: boolean
  onUse: (value: ReportAnswer) => void
}) {
  const [open, setOpen] = useState(false)
  const current = asText(answer)
  const latest = entries[0]

  return (
    <div className="flex flex-col gap-2">
      {/* The last answer here, without a tap. A technician back at a house
          they inspected last year wants to see what was found then while they
          look now; it is shown, never filled in (decisions 55 and 56). */}
      {latest ? (
        <p className="flex items-start gap-1.5 text-[12px] leading-snug text-ink-500">
          <Icon name="refresh" className="mt-px size-3.5 shrink-0" />
          <span>
            Last time: <span className="font-medium text-ink-800">{asText(latest.value)}</span>
            {' · '}
            {formatDate(latest.at)} · {latest.author?.name ?? 'someone'}
          </span>
        </p>
      ) : null}
      {/* With one earlier answer the line above already says all of it, so
          the only thing left to offer is taking it. */}
      {entries.length === 1 ? (
        readOnly || !latest ? null : (
          <button
            type="button"
            disabled={asText(latest.value) === current}
            onClick={() => onUse(latest.value)}
            className="min-h-[var(--control-sm)] self-start rounded-full bg-white px-3 text-[12px] font-medium text-ink-600 ring-1 ring-ink-200 ring-inset transition-colors hover:bg-ink-50 disabled:text-ink-400"
          >
            {asText(latest.value) === current ? 'Same as last time' : 'Use last time’s answer'}
          </button>
        )
      ) : (
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((on) => !on)}
          className={cn(
            'min-h-[var(--control-sm)] self-start rounded-full px-3 text-[12px] font-medium transition-colors',
            open
              ? 'bg-brand-800 text-white ring-1 ring-brand-800 ring-inset'
              : 'bg-white text-ink-600 ring-1 ring-ink-200 ring-inset hover:bg-ink-50',
          )}
        >
          {`Answered here ${entries.length} times before`}
        </button>
      )}

      {open ? (
        <ul aria-label={`Earlier answers for ${label}`} className="flex flex-col gap-1.5">
          {entries.map((entry) => {
            const text = asText(entry.value)
            const same = text === current

            return (
              <li
                key={entry.responseId}
                className="flex flex-col gap-1 rounded-[10px] bg-ink-50 px-3 py-2 ring-1 ring-ink-200 ring-inset"
              >
                <span className="text-[11px] text-ink-500">
                  {formatDate(entry.at)} · {entry.author?.name ?? 'someone'}
                </span>
                <span className="text-[13px] leading-relaxed text-ink-800">{text}</span>
                {readOnly ? null : (
                  <span className="flex flex-wrap gap-2 pt-0.5">
                    <button
                      type="button"
                      disabled={same}
                      onClick={() => onUse(entry.value)}
                      className="text-[12px] font-medium text-ink-600 disabled:text-ink-400 hover:text-ink-900"
                    >
                      {same ? 'This is what you have' : 'Use this'}
                    </button>
                    {appendable && !same && current.length > 0 ? (
                      <button
                        type="button"
                        onClick={() => onUse(`${current.replace(/\s+$/, '')} ${text}`)}
                        className="text-[12px] font-medium text-ink-600 hover:text-ink-900"
                      >
                        Add it to what you have
                      </button>
                    ) : null}
                  </span>
                )}
              </li>
            )
          })}
        </ul>
      ) : null}
    </div>
  )
}

function isBlank(answer: ReportAnswer, photos: ReportPhoto[]): boolean {
  if (photos.length > 0) return false
  if (answer === null || answer === undefined) return true
  if (typeof answer === 'string') return answer.trim().length === 0
  if (Array.isArray(answer)) return answer.length === 0
  // `false` is the answer to "was a nest found", not the absence of one.
  return false
}

function isChosen(answer: ReportAnswer, option: string): boolean {
  return Array.isArray(answer) ? answer.includes(option) : answer === option
}

function dropdownValue(field: ReportField, answer: ReportAnswer): string | string[] {
  if (isMulti(field)) return Array.isArray(answer) ? answer : []
  return typeof answer === 'string' ? answer : ''
}

/** `<input type="date">` wants `yyyy-mm-dd`; the answer is stored as an ISO instant. */
function dateValue(answer: ReportAnswer): string {
  if (typeof answer !== 'string' || answer === '') return ''
  const date = new Date(answer)
  return Number.isNaN(date.getTime()) ? '' : (date.toISOString().slice(0, 10) as string)
}

/**
 * The photos, and the captions that make them evidence.
 *
 * A caption is not decoration here — "3 photos from site" is worth nothing to
 * the office six months later, where "mud leads to pier 3, subfloor" is the
 * finding. So the caption field is on the tile rather than behind a tap.
 */
export function PhotoGrid({
  label,
  photos,
  readOnly,
  compact,
  onAdd,
  onRemove,
  onCaption,
}: {
  label: string
  photos: ReportPhoto[]
  readOnly?: boolean
  /** Beside an answer rather than being one: no empty tile, just a line. */
  compact?: boolean
  onAdd?: () => void
  onRemove?: (photoId: string) => void
  onCaption?: (photoId: string, caption: string) => void
}) {
  return (
    <div role="group" aria-label={label} className="flex flex-col gap-2">
      <div className="grid grid-cols-[repeat(auto-fill,minmax(140px,1fr))] gap-2">
        {photos.map((photo) => (
          <div
            key={photo.id}
            className="flex flex-col gap-1.5 rounded-[10px] bg-ink-50 p-2 ring-1 ring-ink-200 ring-inset"
          >
            <div className="grid aspect-[4/3] place-items-center overflow-hidden rounded-md bg-ink-100">
              {fileUrl(photo.attachment) ? (
                <img
                  src={fileUrl(photo.attachment) ?? ''}
                  alt={photo.caption ?? photo.attachment.filename}
                  className="size-full object-cover"
                />
              ) : (
                <span className="p-2 text-center text-[10px] text-ink-500">
                  Bytes not fetched yet
                </span>
              )}
            </div>
            {onCaption ? (
              <Input
                aria-label={`Caption for ${photo.attachment.filename}`}
                defaultValue={photo.caption ?? ''}
                disabled={readOnly}
                placeholder="What this shows"
                className="text-xs"
                onBlur={(event) => {
                  if (event.target.value !== (photo.caption ?? '')) {
                    onCaption(photo.id, event.target.value)
                  }
                }}
              />
            ) : (
              <p className="text-[11px] font-medium text-ink-600">
                {photo.caption ?? 'No caption'}
              </p>
            )}
            {onRemove && !readOnly ? (
              <button
                type="button"
                onClick={() => onRemove(photo.id)}
                className="self-start text-[11px] font-medium text-ink-500 hover:text-red-700"
              >
                Remove
              </button>
            ) : null}
          </div>
        ))}

        {onAdd && !readOnly && !compact ? (
          <button
            type="button"
            onClick={onAdd}
            className="grid aspect-[4/3] place-items-center rounded-[10px] bg-white text-xs font-semibold text-ink-600 ring-1 ring-ink-300 ring-inset transition-colors hover:bg-ink-50"
          >
            Add photo
          </button>
        ) : null}
      </div>

      {onAdd && !readOnly && compact ? (
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex min-h-[var(--control-sm)] items-center gap-1.5 self-start rounded-full bg-white px-3 text-[12px] font-medium text-ink-600 ring-1 ring-ink-200 ring-inset transition-colors hover:bg-ink-50"
        >
          <CameraGlyph />
          {photos.length === 0 ? 'Add a photo' : 'Add another photo'}
        </button>
      ) : null}

      {photos.length === 0 && !onAdd ? (
        <p className="text-xs text-ink-500">No photos on this report.</p>
      ) : null}
    </div>
  )
}

/** Decoration beside the word, so it is `aria-hidden` and carries no label. */
function CameraGlyph() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="size-3.5"
    >
      <path d="M3 8.5A1.5 1.5 0 0 1 4.5 7h2.2a1 1 0 0 0 .83-.45l.94-1.4A1 1 0 0 1 9.3 4.7h5.4a1 1 0 0 1 .83.45l.94 1.4a1 1 0 0 0 .83.45h2.2A1.5 1.5 0 0 1 21 8.5v9a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 17.5z" />
      <circle cx="12" cy="13" r="3.2" />
    </svg>
  )
}

/**
 * The signature block.
 *
 * A signature is an `Attachment`, so this shows whether one is on the answer
 * and hands the drawing off to whatever captured it. Clearing is separate from
 * signing because a client's signature is the one thing on the report that
 * cannot be recreated from memory.
 */
function SignatureBlock({
  label,
  signed,
  readOnly,
  onSign,
  onClear,
}: {
  label: string
  signed: boolean
  readOnly?: boolean
  onSign?: () => void
  onClear: () => void
}) {
  if (signed) {
    return (
      <div className="flex items-center gap-3 rounded-[10px] bg-brand-50 px-3 py-3 ring-1 ring-brand-200 ring-inset">
        <span className="flex-1 text-[13px] font-semibold text-brand-900">Signed</span>
        {readOnly ? null : (
          <button
            type="button"
            onClick={onClear}
            className="text-[12px] font-medium text-ink-600 hover:text-red-700"
          >
            Clear
          </button>
        )}
      </div>
    )
  }

  if (!onSign || readOnly) {
    return (
      <p className="rounded-[10px] bg-ink-50 px-3 py-3 text-xs text-ink-500 ring-1 ring-ink-200 ring-inset">
        Not signed.
      </p>
    )
  }

  return (
    <button
      type="button"
      onClick={onSign}
      className="h-[76px] rounded-[10px] bg-white text-[13px] font-semibold text-ink-500 ring-1 ring-ink-300 ring-inset transition-colors hover:bg-ink-50"
    >
      {label}
    </button>
  )
}
